import { Conversation } from '@elevenlabs/client';
import { escapeHTML } from '../../shared/utils/sanitize';
import { Nullable } from '../types';
import { ResponseMessage } from './types';

let conversation: Nullable<Conversation>;
let lastConversationId: string | null = null;
let transcriptMessages: ResponseMessage[] = [];
let careerIntentHandled = false;

const CAREER_REDIRECT_MESSAGE = 'Please visit the career page for job related queries.';
const CAREER_INTENT_PATTERN =
  /\b(job|jobs|career|careers|hiring|hire|vacancy|vacancies|opening|openings|recruitment|recruiter|apply|resume|cv|internship|intern|interview)\b/i;

type CareerIntentHandler = () => void | Promise<void>;

function isUserMessage(message: ResponseMessage) {
  const source = String(message.source || '').toLowerCase();
  return source === 'user' || source === 'human' || source.includes('user');
}

function isCareerIntent(message: string) {
  return CAREER_INTENT_PATTERN.test(message);
}

function appendTranscriptMessage(source: string, message: string, messagesContainer: HTMLDivElement) {
  transcriptMessages.push({ source, message });
  renderTranscript(messagesContainer);
}

function scheduleCareerDisconnect(status: HTMLParagraphElement, onCareerIntent?: CareerIntentHandler) {
  careerIntentHandled = true;
  status.textContent = 'Disconnected';

  if (!onCareerIntent) return;

  window.setTimeout(() => {
    void onCareerIntent();
  }, 100);
}

function handleCareerIntent(
  message: ResponseMessage,
  messagesContainer: HTMLDivElement,
  status: HTMLParagraphElement,
  onCareerIntent?: CareerIntentHandler
) {
  if (careerIntentHandled || !isUserMessage(message) || !isCareerIntent(message.message)) return false;

  appendTranscriptMessage('assistant', CAREER_REDIRECT_MESSAGE, messagesContainer);
  scheduleCareerDisconnect(status, onCareerIntent);
  return true;
}

function renderTranscript(messagesContainer: HTMLDivElement) {
  const documentFragment = document.createDocumentFragment();

  transcriptMessages.forEach((message) => {
    if (!message.message.trim()) return;

    const paragraph = document.createElement('p');
    const safeSource = escapeHTML(message.source);
    const safeMessage = escapeHTML(message.message);
    paragraph.innerHTML = `<strong>${safeSource}</strong> : ${safeMessage}`;
    documentFragment.appendChild(paragraph);
  });

  messagesContainer.innerHTML = '';
  messagesContainer.appendChild(documentFragment);
  messagesContainer.scrollTop = messagesContainer.scrollHeight;
}

/**
 * @description start conversation with eleven labs agent using a signed URL
 * @param signedUrl signed URL from the backend (replaces direct agent-id)
 * @param status html element to display connection status
 */
async function startConversation(
  stream: MediaStream | null,
  signedUrl: string,
  status: HTMLParagraphElement,
  messagesContainer: HTMLDivElement,
  micButton: HTMLButtonElement,
  onCareerIntent?: CareerIntentHandler
): Promise<void> {
  try {
    if (!conversation) {
      transcriptMessages = [];
      lastConversationId = null;
      careerIntentHandled = false;

      // Launch the session using signed URL (no API key or agent-id exposed)
      conversation = await Conversation.startSession({
        signedUrl: signedUrl,

        onConnect: (props: any) => {
          status.textContent = 'Connected';
          micButton.disabled = true;
          // Capture conversation ID if available
          if (props?.conversationId) {
            lastConversationId = props.conversationId;
          }
        },
        onDisconnect: () => {
          status.textContent = 'Disconnected';
          micButton.disabled = careerIntentHandled;
        },
        onError: (err: any) => {
          console.error('Conversation error:', err);
          micButton.disabled = false;
        },
        onMessage(props) {
          if (careerIntentHandled && !isUserMessage(props)) return;

          transcriptMessages.push(props);
          renderTranscript(messagesContainer);
          if (handleCareerIntent(props, messagesContainer, status, onCareerIntent)) {
            micButton.disabled = true;
          }
        },

        //@ts-ignore
        onModeChange: ({ mode }) => {
          if (careerIntentHandled) return;

          // mode is 'listening' or 'speaking'
          status.textContent = mode as string;
          if (status.textContent === 'listening') {
            micButton.disabled = false;
          }
        },
      });

      conversation.sendUserMessage('');

      // Try to extract conversation ID using the SDK's getId() method
      if (!lastConversationId && conversation) {
        try {
          lastConversationId = conversation.getId();
        } catch {
          lastConversationId = null;
        }
      }
    }
  } catch (err) {
    console.error('Failed to start conversation:', err);
    conversation = null;
    throw err;
  }
}

function sendTextToVoiceAgent(
  input: HTMLInputElement,
  messagesContainer?: HTMLDivElement,
  status?: HTMLParagraphElement,
  onCareerIntent?: CareerIntentHandler
) {
  const text = input.value.trim();
  if (text !== '') {
    // Append typed message to voice transcript so user can see what they sent
    if (messagesContainer) {
      const message = { source: 'user', message: text };
      appendTranscriptMessage(message.source, message.message, messagesContainer);
      if (status && handleCareerIntent(message, messagesContainer, status, onCareerIntent)) {
        input.value = '';
        return;
      }
    }

    if (conversation) conversation.sendUserMessage(text);
    input.value = '';
  }
}

/**
 * @description get the conversation ID of the last/current conversation
 */
function getConversationId(): string | null {
  if (lastConversationId) return lastConversationId;
  if (conversation) {
    try {
      return conversation.getId();
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * @description stop the conversation
 */
async function stopConversation(): Promise<void> {
  if (conversation) {
    await conversation.endSession();
    conversation = null;
  }
  careerIntentHandled = false;
}

export { getConversationId, sendTextToVoiceAgent, startConversation, stopConversation };
